import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import Snackbar from "@material-ui/core/Snackbar";
import Button from "@material-ui/core/Button";
import CircularProgress from '@material-ui/core/CircularProgress';
import classNames from 'classnames';
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import CloseIcon from "@material-ui/icons/Close";
import green from "@material-ui/core/colors/green";
import amber from "@material-ui/core/colors/amber";
import IconButton from "@material-ui/core/IconButton";
import SnackbarContent from "@material-ui/core/SnackbarContent";
import WarningIcon from "@material-ui/icons/Warning";
import { withStyles } from "@material-ui/core/styles";

const variantIcon = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon
};

const snackbarContentStyle = theme => ({
  root: {
    fontFamily: '"Work Sans", Arial, sans-serif',
    fontSize: '1em'
  },
  success: {
    backgroundColor: green[600]
  },
  error: {
    backgroundColor: theme.palette.error.dark
  },
  info: {
    backgroundColor: theme.palette.primary.dark
  },
  progress: {
    backgroundColor: theme.palette.primary.dark
  },
  warning: {
    backgroundColor: amber[700]
  },
  icon: {
    fontSize: 20
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing.unit
  },
  message: {
    display: "flex",
    alignItems: "center",
    padding: '8px'
  },
  circularProgress: {
    color: 'white'
  },
  circularProgressContainer: {
    marginRight: theme.spacing.unit * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  close: {
    border: 'none !important',
    color: 'inherit !important',
    padding: 'none !important',
    // borderRadius: 'inherit !important',
    // backgroundColor: 'inherit !important',
    minWidth: 'inherit !important',
  }
});

function MySnackbarContent(props) {
  const { classes, className, message, canBeDismissed, onClose, variant, ...other } = props;
  const Icon = variantIcon[variant];

  return (
    <SnackbarContent
      className={classNames(classes.root, classes[variant], className)}
      aria-describedby="client-snackbar"
      message={
        <span id="client-snackbar" className={classes.message}>
          {variant === 'progress' ? (
            <div className={classes.circularProgressContainer}>
            <CircularProgress className={classes.circularProgress} size={20}/>
            </div>
          ) : (
            <Icon className={classNames(classes.icon, classes.iconVariant)} />
          )}
          {message}
        </span>
      }
      action={canBeDismissed ? [
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          className={classes.close}
          onClick={onClose}
        >
          <CloseIcon className={classes.icon} />
        </IconButton>
      ] : []}
      {...other}
    />
  );
}

MySnackbarContent.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  message: PropTypes.node,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(["success", "warning", "error", "info", "progress"]).isRequired
};

const MySnackbarContentWrapper = withStyles(snackbarContentStyle)(MySnackbarContent);

export default class SnackbarProvider extends PureComponent {
  state = {
    message: null,
    open: false
  };

  getChildContext() {
    return {
      snackbar: {
        showMessage: this.showMessage,
      }
    };
  }

  /**
   * Display a message with this snackbar.
   * @param {string} message message to display
   * @param {string} [action] label for the action button
   * @param {function} [handleAction] click handler for the action button
   * @public
   */
  showMessage = (message, variant, canBeDismissed = true, action = undefined, handleAction = undefined) => {
    this.setState({ open: true, message, action, canBeDismissed, handleAction, variant });
  };

  handleActionClick = () => {
    this.handleClose();
    this.state.handleAction();
  };

  handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    this.setState({ open: false });
  };


  render() {
    const { action, message, open, variant, canBeDismissed } = this.state;

    const { children, SnackbarProps = {} } = this.props;

    return (
      <React.Fragment>
        {children}
        <Snackbar
          {...SnackbarProps}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          open={open}
          onClose={this.handleClose}
        >
          <MySnackbarContentWrapper
            onClose={this.handleClose}
            variant={variant}
            message={message}
            canBeDismissed={canBeDismissed}
          />
        </Snackbar>
      </React.Fragment>
    );
  }
}

SnackbarProvider.childContextTypes = {
  snackbar: PropTypes.shape({
    showMessage: PropTypes.func
  })
};

SnackbarProvider.propTypes = {
  /**
   * The children that are wrapped by this provider.
   */
  children: PropTypes.node,
  /**
   * Props to pass through to the snackbar.
   */
  SnackbarProps: PropTypes.object
};
